import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';
import {
  OctagonMinusIcon,
  PlusIcon,
  PowerIcon,
  ScanBarcodeIcon,
  ShoppingBagIcon,
  StoreIcon,
  TagIcon,
} from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';

import { OrderHoldType } from '@invenco/common-domain/enums';
import { OrderStatus } from '@invenco/sales-interface/interfaces';

import { Badge } from '@/components/core/badge';
import { Button } from '@/components/core/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/core/tooltip';
import { DataImporter } from '@/components/data-importer/data-importer';
import { TemplateKey } from '@/components/data-importer/types';
import {
  DataTable,
  DataTableFilterMenu,
  DataTablePageContent,
  DataTablePagination,
  DataTableRefreshMenu,
  DataTableToolbar,
  DataTableViewMenu,
  type FilterDefs,
  type FilterState,
  useDataTable,
  useDataTableState,
  useDataTableViews,
  useRowNavigationCallback,
  View,
} from '@/components/data-table';
import { Header, HeaderActions, HeaderIcon, HeaderTitle } from '@/components/header';
import { Page } from '@/components/layout';
import { StatusBadge } from '@/components/status-badge';
import { DATA_POLLING_DURATION_MS, LIVE_DATA_INTERVAL_MS } from '@/constants';
import { useGateways } from '@/gateways/GatewayProvider';
import { OrderSortField, QueryOrdersProps } from '@/gateways/impl/SalesGateway';
import { formatDateTime, formatMoney, formatNumber } from '@/shared/helpers';
import { useQueryWithInput } from '@/shared/hooks/queries';
import { usePulseFlag } from '@/shared/hooks/use-pulse-flag';
import { orderStatusType } from '@/shared/status-maps';
import { orderHoldSubtypeTitle, orderHoldTypeTitle, orderStatusTitle } from '@/shared/title-maps';

import { OrderListItem, OrderListMapper } from './order-list-mapper';

const columnHelper = createColumnHelper<OrderListItem>();

const columns = [
  columnHelper.accessor('name', { header: 'Name' }),
  columnHelper.accessor('reference', { header: 'Reference', meta: { truncate: true } }),
  columnHelper.accessor('customerName', { header: 'Customer' }),
  columnHelper.accessor('channelName', { header: 'Channel' }),
  columnHelper.accessor('status', {
    header: 'Status',
    cell: ({ getValue, row }) => {
      const status = getValue();
      if (!status) return null;
      return (
        <div className="flex items-center gap-2">
          <StatusBadge status={status} type={orderStatusType} title={orderStatusTitle} />
          {row.original.orderHolds?.map(({ id, type, subtype, description, createdAt }) => (
            <Tooltip key={id}>
              <TooltipTrigger>
                <Badge variant="secondary">{orderHoldTypeTitle[type] || type}</Badge>
              </TooltipTrigger>
              <TooltipContent>
                <div className="flex flex-col gap-2">
                  {subtype && (
                    <span className="font-semibold">
                      {orderHoldSubtypeTitle[subtype] || subtype}
                    </span>
                  )}
                  <span>{formatDateTime(createdAt)}</span>
                  {description && <span>{description}</span>}
                </div>
              </TooltipContent>
            </Tooltip>
          ))}
        </div>
      );
    },
  }),
  columnHelper.accessor('qtyOrdered', {
    header: 'Quantity',
    cell: ({ getValue }) => formatNumber(getValue()),
    meta: { align: 'right' },
  }),
  columnHelper.accessor('totalPrice', {
    header: 'Total',
    cell: ({ getValue, row }) => {
      const value = getValue();
      return value ? formatMoney(value, row.original.currency) : '';
    },
    meta: { align: 'right' },
  }),
  columnHelper.accessor('orderedAt', {
    header: 'Ordered',
    cell: ({ getValue }) => {
      const value = getValue();
      return value ? formatDateTime(value) : '';
    },
  }),
];

const presetViews: View[] = [
  { id: 'all', name: 'All' },
  ...[
    OrderStatus.DRAFT,
    OrderStatus.OPEN,
    OrderStatus.HOLD,
    OrderStatus.CLOSED,
    OrderStatus.CANCELLED,
  ].map((status) => ({
    id: status,
    name: orderStatusTitle[status],
    state: {
      filters: [{ id: 'status', operator: 'in', value: [status] }],
    },
  })),
];

const tieBreakerSort = {
  key: 'createdAt',
  direction: 'desc',
} as const;

const convertOldFilters = (filters: Record<string, any>) => {
  const filterState: FilterState<FilterDefs> = [];
  if (filters.status) {
    filterState.push({
      id: 'status',
      operator: 'in',
      value: [filters.status.value],
    });
  }
  if (filters.skuNames) {
    filterState.push({
      id: 'skuName',
      operator: 'in',
      value: (Array.isArray(filters.skuNames) ? filters.skuNames : [filters.skuNames]).map(
        ({ value }) => value,
      ),
    });
  }
  if (filters.tagNames) {
    filterState.push({
      id: 'tagName',
      operator: 'in',
      value: (Array.isArray(filters.tagNames) ? filters.tagNames : [filters.tagNames]).map(
        ({ value }) => value,
      ),
    });
  }
  if (filters.channelNames) {
    filterState.push({
      id: 'channelName',
      operator: 'in',
      value: (Array.isArray(filters.channelNames)
        ? filters.channelNames
        : [filters.channelNames]
      ).map(({ value }) => value),
    });
  }
  return filterState;
};

export function OrderList() {
  const { salesGateway, productsGateway } = useGateways();
  const [isLive, setIsLive] = useState(false);
  const [isPolling, triggerPolling] = usePulseFlag(DATA_POLLING_DURATION_MS);
  const onClickRow = useRowNavigationCallback<OrderListItem>('/sales/orders');

  const { views, isLoadingSavedViews, ...viewTabsProps } = useDataTableViews({
    tableId: 'orders',
    presetViews,
    convertOldFilters,
  });

  const { data: channelOptions } = useQuery({
    queryKey: ['channels'],
    queryFn: ({ signal }) => salesGateway.getChannels(undefined, { signal }),
    select: ({ items }) => items.map((c) => c.name),
  });

  const { state, currentView, onChangeViewId, filters, ...tableProps } = useDataTableState({
    columns,
    filterDefs: {
      status: {
        label: 'Status',
        variant: 'multiselect',
        icon: PowerIcon,
        options: [
          OrderStatus.DRAFT,
          OrderStatus.PENDING,
          OrderStatus.OPEN,
          OrderStatus.HOLD,
          OrderStatus.CLOSED,
          OrderStatus.CANCELLED,
        ],
        optionLabel: (option) => orderStatusTitle[option as OrderStatus] || option,
      },
      skuName: {
        label: 'SKU',
        variant: 'multiselect',
        icon: ScanBarcodeIcon,
        options: async (input, options) => {
          const result = await productsGateway.getSkus(input, options);
          return {
            items: result.items.map((sku) => sku.name),
          };
        },
      },
      tagName: {
        label: 'Tags',
        variant: 'multiselect',
        icon: TagIcon,
        options: async (input, options) => {
          const result = await salesGateway.getTags(input, options);
          return {
            items: result.items.map((tag) => tag.name),
          };
        },
      },
      channelName: {
        label: 'Channel',
        variant: 'multiselect',
        icon: StoreIcon,
        options: channelOptions ?? [],
      },
      holdType: {
        label: 'Hold type',
        variant: 'multiselect',
        icon: OctagonMinusIcon,
        options: Object.values(OrderHoldType),
        optionLabel: (option) => orderHoldTypeTitle[option as OrderHoldType] || option,
      },
    },
    views,
    isLoadingSavedViews,
  });

  const queryProps: QueryOrdersProps = {
    take: state.pagination.pageSize,
    offset: state.pagination.pageIndex * state.pagination.pageSize,
    sortBy: [
      ...state.sorting.map((s) => ({
        key: s.id as OrderSortField,
        direction: s.desc ? ('desc' as const) : ('asc' as const),
      })),
      tieBreakerSort,
    ],
    search: filters.search,
    filters: filters.getActiveFilterMap(),
  };

  const { data, isLoading, isFetching, isPlaceholderData, isError, refetch } = useQueryWithInput({
    parentKey: 'orders',
    input: queryProps,
    query: (input, { signal }) => salesGateway.queryOrders(input, { signal }),
    select: OrderListMapper.toData,
    refetchInterval: isLive || isPolling ? LIVE_DATA_INTERVAL_MS : undefined,
    placeholderData: keepPreviousData,
  });

  const table = useDataTable({
    columns,
    data: data?.data ?? [],
    rowCount: data?.total,
    isLoading,
    isLoadingWithStaleData: isFetching && isPlaceholderData,
    hasError: isError,
    state,
    ...tableProps,
  });

  return (
    <Page>
      <Header>
        <HeaderIcon>
          <ShoppingBagIcon />
        </HeaderIcon>
        <HeaderTitle>Orders</HeaderTitle>
        <HeaderActions>
          <DataImporter template={TemplateKey.ORDERS} onComplete={triggerPolling} />
          <Button asChild>
            <Link to="/sales/orders/new">
              <PlusIcon />
              Create
            </Link>
          </Button>
        </HeaderActions>
      </Header>
      <DataTablePageContent>
        <DataTableToolbar>
          <DataTableViewMenu
            views={views}
            currentView={currentView}
            filterState={filters.filterState}
            rowCount={data?.total}
            isLoadingRowCount={isLoading || isPlaceholderData}
            onChangeViewId={onChangeViewId}
            {...viewTabsProps}
          />
          <DataTableFilterMenu filters={filters} />
          <DataTableRefreshMenu onRefresh={refetch} isLive={isLive} onChangeLive={setIsLive} />
        </DataTableToolbar>
        <DataTable table={table} onClickRow={onClickRow} />
        <DataTablePagination table={table} />
      </DataTablePageContent>
    </Page>
  );
}
